import posthog, { type Properties } from 'posthog-js';
import { isValidPostHogKey, posthogBeforeSend } from '@etiquetachef/types';

// Wrapper PostHog do admin (admin.etiquetachef.com). Idêntico em
// princípios ao do app cliente, mas com 2 diferenças:
//   - admin não tem `tenantId` (super-admin não pertence a um tenant);
//     group analytics fica desativado;
//   - userRole é fixo em `super-admin` quando autorizado.
//
// Privacidade: emails @etiquetachef.com (próprios), mas o admin EXIBE dado de
// tenants/clientes (nomes, e-mails, telefones das mensagens de contato) e
// credenciais (o `client_secret` da API pública, uma única vez). Este
// parágrafo pedia `ph-no-capture` nessas telas e a classe nunca foi aplicada
// — então, desde a #795 (OBS-2/OBS-6), a proteção é de configuração, não de
// disciplina de marcação: replay desligado, autocapture sem texto nem
// atributos e `before_send` redigindo URL/contato. Ver `initPostHog`.

const TAG = 'admin/posthog';

/**
 * Origem dos eventos do admin.
 *
 * Deliberadamente FORA de `PRODUCT_SURFACES` (`aggregatePostHog.ts`): o
 * super-admin não é cliente, e contá-lo no DAU do produto inflaria a métrica
 * com o próprio time — foi o bug #292. Ter a marca é o que permite excluí-lo
 * de propósito, em vez de por acidente.
 */
const POSTHOG_SURFACE = 'web-admin';

/**
 * Super properties de todo evento: registradas no `loaded` e de novo depois de
 * cada `posthog.reset()`, que as apaga (#1036). Uma constante só para os dois
 * pontos não divergirem.
 */
const SUPER_PROPERTIES = { surface: POSTHOG_SURFACE } as const satisfies Properties;

type RawEnv = Record<string, string | undefined>;
const env = import.meta.env as unknown as RawEnv;

const POSTHOG_KEY = env.VITE_POSTHOG_KEY ?? '';
// Checagem explícita de vazio, não `??`: secret não configurado no GitHub
// Actions chega ao build como string VAZIA, não como undefined — e `??` só
// captura null/undefined. Com `??` o host viraria '' e o SDK apontaria para
// lugar nenhum, em silêncio. Mesma família do bug da chave (#267): presença
// não é validade. (`||` resolveria, mas o lint exige `??` — daí a forma longa.)
const RAW_POSTHOG_HOST = env.VITE_POSTHOG_HOST ?? '';
const POSTHOG_HOST = RAW_POSTHOG_HOST === '' ? 'https://us.i.posthog.com' : RAW_POSTHOG_HOST;

let initialized = false;
let lastIdentifiedUid: string | null = null;

/**
 * `true` quando `VITE_POSTHOG_KEY` tem formato de project key (`phc_…`).
 *
 * Origem: Sprint 9 PR A — commit d0cd2617f, 2026-04-28 17:11 +0200 (sem issue).
 *
 * Usado por: `initPostHog` (este arquivo). Falha conhecida em #267 (o secret
 * `"-"` passava num guard de presença).
 */
export function isPostHogEnabled(): boolean {
  // Formato, não presença (#267): o secret valeu `"-"` por meses e um
  // hífen passava no `length > 0` — o init rodava com chave inválida e
  // o aviso abaixo nunca aparecia.
  return isValidPostHogKey(POSTHOG_KEY);
}

/**
 * Inicializa o PostHog do admin (idempotente; no-op sem chave válida).
 *
 * Origem: Sprint 9 PR A — commit d0cd2617f, 2026-04-28 17:11 +0200 (sem issue).
 *
 * Por quê: o admin não tem acesso a DevTools do operador; o PostHog é o canal
 * de erro (`reportFatal`/`reportNonFatal`, `capture_exceptions`) e de uso.
 *
 * Contrato: `posthog.init` uma vez; `surface: 'web-admin'` registrado no
 * `loaded`. NÃO grava replay. Autocapture conta o clique sem levar texto nem
 * atributos do elemento; todo evento passa por `redactPostHogCapture`.
 *
 * Usado por: `src/main.tsx` (boot).
 *
 * Arquitetura: só SDK — o projeto PostHog (399404) não é tocado. Nenhum painel
 * lê `$el_text`/`$elements` nem replay do admin (`docs/analytics-playbook.md`;
 * a `surface` `web-admin` fica fora de `PRODUCT_SURFACES`, #292).
 *
 * Falhas conhecidas:
 *  - 2026-10-03 (#795, OBS-2): replay ligado só com `maskAllInputs` — texto
 *    renderizado ia cru, inclusive o `client_secret` que o
 *    `ApiKeyCreatedModal` mostra como texto (Cloudscape `CopyToClipboard`
 *    inline) e o PII de tenants. O `ph-no-capture` prometido no cabeçalho
 *    nunca foi aplicado.
 *  - 2026-10-03 (#795, OBS-6): autocapture sem máscara mandava texto e `href`
 *    (`mailto:`/`tel:` da `ContactMessagesPage`) do elemento clicado.
 *
 * Dicas: `mask_all_element_attributes` NÃO cobre o `href` — o SDK o anexa por
 * fora da máscara; quem cobre é o `before_send`. Religar replay é decisão do
 * owner (aviso na política de privacidade) e, se acontecer, o
 * `session_recording` abaixo já mascara todo texto. Teste: `posthog.test.ts`.
 *
 * Histórico:
 *  - 2026-04-28 17:11 +0200 — (Sprint 9 PR A) criado.
 *  - 2026-10-03 14:32 +0200 — (#795, OBS-2/OBS-6) replay desligado,
 *    `maskTextSelector: '*'`, `mask_all_text`, `mask_all_element_attributes` e
 *    `before_send` de redação.
 */
export function initPostHog(): void {
  if (initialized) return;
  if (!isPostHogEnabled()) {
    console.info(
      `${TAG}: VITE_POSTHOG_KEY ausente ou inválida (esperado phc_…), telemetria desativada`,
    );
    return;
  }
  posthog.init(POSTHOG_KEY, {
    api_host: POSTHOG_HOST,
    person_profiles: 'identified_only',
    capture_pageview: true,
    capture_pageleave: true,
    // Error Tracking (#12 §3.4.3): captura exceções não tratadas e
    // promise rejections automaticamente, complementando o report
    // explícito via `reportFatal`/`reportNonFatal` + ErrorBoundary.
    capture_exceptions: true,
    autocapture: {
      dom_event_allowlist: ['click', 'change', 'submit'],
    },
    // Bug corrigido em 2026-10-03 14:32 +0200 (#795, OBS-6): o autocapture
    // mandava o texto e os atributos do elemento clicado — e-mail/telefone de
    // cliente (`ContactMessagesPage`), nome de tenant. O clique segue contado
    // (tag, posição, classes), sem o conteúdo. Nenhum painel lê `$el_text` do admin.
    mask_all_text: true,
    mask_all_element_attributes: true,
    // Bug corrigido em 2026-10-03 14:32 +0200 (#795, OBS-1/OBS-6): o `href` do
    // link clicado (`mailto:`/`tel:`) sai mesmo com a máscara de atributos (o
    // SDK o anexa por fora dela); a redação é o ponto único por onde passa todo
    // evento. Mesma função do app e do CRM.
    // Bug corrigido em 2026-10-13 11:23 +0200 (#315): o aviso inofensivo
    // `ResizeObserver loop completed…` do navegador saía como exceção fatal e
    // disparava o alerta "Exceção fatal no web". `posthogBeforeSend` descarta
    // esse ruído e depois redige. Não voltar a passar `redactPostHogCapture` cru.
    before_send: posthogBeforeSend,
    // Bug corrigido em 2026-10-03 14:32 +0200 (#795, OBS-2): o replay gravava
    // o texto da tela cru (só input era mascarado) — inclusive o
    // `client_secret` da API pública, exibido como texto no
    // `ApiKeyCreatedModal`, e o PII de tenants que o super-admin navega. Mesma
    // postura do CRM e do portal de desenvolvedores: replay desligado. Não
    // religar sem decisão do owner.
    disable_session_recording: true,
    // Segunda barreira, caso alguém religue o replay (ou chame
    // `startSessionRecording`): todo texto mascarado, e-mail inclusive.
    session_recording: {
      maskAllInputs: true,
      maskTextSelector: '*',
      maskInputOptions: {
        password: true,
        email: true,
      },
      recordCrossOriginIframes: false,
    },
    persistence: 'localStorage+cookie',
    loaded: (instance) => {
      // Origem do evento. O admin era o único SPA que não registrava: seus
      // eventos caíam no balde do `$host` nas auditorias (`audit-events.ts`
      // mostrava `admin.etiquetachef.com`, não `web-admin`) e não dava para
      // separá-los por superfície como se faz com os demais. Registrado no
      // `loaded` porque o SDK chama esse callback ANTES do `$pageview`
      // inicial — depois do `init` seria uma corrida com o timer dele.
      instance.register(SUPER_PROPERTIES);
      console.info(`${TAG}: initialized host=${POSTHOG_HOST}`);
      if (env.DEV === 'true' || env.MODE === 'development') {
        instance.debug();
      }
    },
  });
  initialized = true;
}

export type IdentifyAdminProps = {
  userId: string;
  email: string;
  appVersion: string;
};

/**
 * Identifica o super-admin logado no PostHog (sem group analytics).
 *
 * Origem: Sprint 9 PR A — commit d0cd2617f, 2026-04-28 17:11 +0200 (sem issue).
 *
 * Contrato: no-op antes do init. Primeira vez para o uid: `identify`; mesma
 * pessoa: `setPersonProperties`. `userRole` fixo `super-admin`, `platform`
 * `web-admin`. Sem `$groups` — super-admin não pertence a tenant.
 *
 * Usado por: `auth/AuthProvider.tsx` (login autorizado).
 *
 * Dados — person properties (anonimizado):
 *   identify("uid-1", { "email": "admin-1@exemplo.com", "userRole": "super-admin",
 *     "appVersion": "1.2.3", "platform": "web-admin" })
 * E-mail é do time (domínio próprio), não de cliente.
 *
 * Histórico:
 *  - 2026-10-10 15:12 +0200 — (#936) cabeçalho padrão (#796).
 */
export function identifyAdmin(props: IdentifyAdminProps): void {
  if (!initialized) return;
  if (lastIdentifiedUid === props.userId) {
    posthog.setPersonProperties({
      email: props.email,
      userRole: 'super-admin',
      appVersion: props.appVersion,
      platform: 'web-admin',
    } satisfies Properties);
    return;
  }
  lastIdentifiedUid = props.userId;
  posthog.identify(props.userId, {
    email: props.email,
    userRole: 'super-admin',
    appVersion: props.appVersion,
    platform: 'web-admin',
  } satisfies Properties);
}

/**
 * Logout: zera a identidade (`posthog.reset()`) se alguém estava identificado.
 *
 * Origem: Sprint 9 PR A — commit d0cd2617f, 2026-04-28 17:11 +0200 (sem issue).
 *
 * Contrato: depois do `reset`, re-registra `SUPER_PROPERTIES` (`surface`), que o
 * `reset` apaga (#1036).
 *
 * Usado por: `auth/AuthProvider.tsx` (sign-out e conta sem claim de admin).
 *
 * Histórico:
 *  - 2026-10-26 20:20 +0100 — (#1036) re-registra `surface` depois do reset.
 */
export function resetPostHog(): void {
  if (!initialized) return;
  if (lastIdentifiedUid === null) return;
  console.info(`${TAG}: reset`);
  lastIdentifiedUid = null;
  posthog.reset();
  // Bug corrigido em 2026-10-26 20:20 +0100 (#1036): `posthog.reset()` limpa a
  // persistência inteira, super properties do `register` inclusive. Sem isto,
  // depois de logout + login na mesma aba todo evento saía sem `surface` (64
  // eventos no admin em 2026-10-21) e sumia dos painéis por superfície. Não
  // tirar: o `loaded` só roda uma vez por carregamento de página.
  posthog.register(SUPER_PROPERTIES);
}

/**
 * Emite um evento do admin no PostHog (no-op antes do init).
 *
 * Origem: Sprint 9 PR A — commit d0cd2617f, 2026-04-28 17:11 +0200 (sem issue).
 *
 * Contrato: sem fila — evento antes do `initPostHog` é perdido. Não há gate
 * de consentimento no admin (ferramenta interna; init no boot). Redação de
 * URL/contato pelo `before_send` (`posthogBeforeSend` → `redactPostHogCapture`); `surface:
 * 'web-admin'` vem do `register` no `loaded`. Não lança.
 *
 * Usado por: `lib/events.ts` (fachada tipada: login, suspensão de conta,
 * faturas…) e `lib/errorReport.ts` (`reportFatal`/`reportNonFatal`).
 *
 * Dados — evento como chega (anonimizado):
 *   { "event": "invoice_marked_paid", "distinct_id": "uid-1",
 *     "properties": { "tenantId": "tenant-1", "invoiceId": "inv-1",
 *       "surface": "web-admin" } }
 * Catálogo: `docs/analytics-playbook.md`.
 *
 * Histórico:
 *  - 2026-10-10 15:12 +0200 — (#936) cabeçalho padrão (#796).
 */
export function captureEvent(event: string, props?: Properties): void {
  if (!initialized) return;
  posthog.capture(event, props);
}

/**
 * Envia uma exceção (`$exception`) ao PostHog — o par de cada report de erro.
 *
 * Origem: #315 — 2026-10-09 16:41 +0200 (0.5, Error Tracking do web).
 *
 * Por quê: a tela Error Tracking só lista `$exception`; o `captureEvent` dos
 * reports não aparece lá (ver `errorReport.ts`). No-op antes do init, como
 * `captureEvent` — o admin não tem canal sem consentimento.
 *
 * Usado por: `errorReport.ts` (`reportFatal`/`reportNonFatal`), só ele.
 */
export function captureException(error: Error, props?: Properties): void {
  if (!initialized) return;
  posthog.captureException(error, props);
}

/** Desliga a captura do SDK; sem consumidor no admin hoje (`rg optOutTracking`). */
export function optOutTracking(): void {
  if (!initialized) return;
  posthog.opt_out_capturing();
}

/** Religa a captura do SDK; sem consumidor no admin hoje (`rg optInTracking`). */
export function optInTracking(): void {
  if (!initialized) return;
  posthog.opt_in_capturing();
}
